import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, interval, takeUntil } from 'rxjs';
import { DataserviceService } from './dataservice.service';
import { environment } from './environments/environment';
import { isCompanyAdminUser } from './utils/company-admin.util';
import { ChatService } from './services/chat.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {

  isCollapsed = false;
  isMobile: boolean = false;
  chatUnreadCount = 0;
  get currentUser(): any {
    return JSON.parse(localStorage.getItem('user') || '{}');
  }

  private readonly destroy$ = new Subject<void>();
  private readonly baseDocumentTitle = document.title || 'KnockVia';

  constructor(
    public router: Router,
    private data: DataserviceService,
    private chatService: ChatService) {}

  ngOnInit(): void {
    this.bindChatUnreadPolling();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user?.crm_id ? String(user.crm_id) : '';
    const hiddenConversationKey = userId
      ? `chat_hidden_conversations_${userId}`
      : '';
    const hiddenConversationValue = hiddenConversationKey
      ? localStorage.getItem(hiddenConversationKey)
      : null;

    localStorage.removeItem('accessToken');

    localStorage.clear();

    if (hiddenConversationKey && hiddenConversationValue !== null) {
      localStorage.setItem(hiddenConversationKey, hiddenConversationValue);
    }

    this.setDocumentTitle(0);

    this.router.navigateByUrl('/signin').then((didNavigate) => {
      if (!didNavigate) {
        window.location.href = '/signin';
      }
    });
  }

  canSeePayroll() {
    return environment.showPayroll;
  }

  payCompDirectly() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.pay_company_directly
  }

  hasProduct(product: string): boolean {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.super_admin) {
      return true;
    }
    return user.providers?.some((p: any) => p.name === product) ?? false;
  }

  hasPermission(permission: string): boolean {
    const user = JSON.parse(localStorage.getItem('user') || '{}');   
    if (user.super_admin) {
      return true;
    }
    return user?.permissions?.some((p: any) => p.name === permission) ?? false;
  }

  checkCompanyPay() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.pay_company_directly;
  }

  checkAdmin(): boolean {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.super_admin;
  }

  isCompanyAdmin(): boolean {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return isCompanyAdminUser(user);
  }

  showSubMenu(permission: string): boolean {
    let allowedPermissions;

    if (permission == 'manager_tools') {
      allowedPermissions = ['view_imports', 'view_groups', 'view_users', 'view_companies', 'manage_markets', 'view_onboarding_board', 'view_onboarding_management'];
    }
    if (permission == 'admin_tools') {
      allowedPermissions = [
        'manage_payroll',
        'manage_payplans',
        'upload_documents',
        'view_documents',
        'assign_signatures',
        'view_states',
        'view_permissions',
        'view_onboarding_board',
        'view_onboarding_management',
      ];
    }
    if (permission == 'document_management') {
      allowedPermissions = ['upload_documents', 'view_documents', 'assign_signatures'];
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.super_admin) {
      return true;
    }
    return allowedPermissions?.some((permission) => user?.permissions?.some((p: any) => p.name === permission)) ?? false;
  }

  isImpersonating(): boolean {
    return localStorage.getItem('isImpersonating') === 'true';
  }

  stopImpersonating() {
    // Store the current URL
    const currentUrl = window.location.href;

    // Restore original super admin data
    localStorage.setItem('user', localStorage.getItem('originalUser')!);
    localStorage.setItem('access_token', localStorage.getItem('originalToken')!);

    // Clean up impersonation data
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('originalToken');

    // Redirect to the stored URL to update all components
    window.location.href = currentUrl;
  }

  openMapFilter() {
    window.dispatchEvent(new Event('openMapDrawer'));
  }

  private bindChatUnreadPolling(): void {
    if (!this.hasPermission('view_chat')) {
      return;
    }
    const userId = this.resolveUserIdentifier();
    if (!userId) {
      return;
    }
    this.refreshChatUnreadSummary(userId);

    interval(15000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshChatUnreadSummary(userId));

    this.chatService
      .onUnreadRefresh()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshChatUnreadSummary(userId));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refreshChatUnreadSummary(userId));
  }

  private refreshChatUnreadSummary(userId: string): void {
    this.chatService.getUnreadSummary(userId).subscribe({
      next: (response) => {
        this.chatUnreadCount = response.totalUnread ?? 0;
        this.setDocumentTitle(this.chatUnreadCount);
      },
      error: () => {
        this.chatUnreadCount = 0;
        this.setDocumentTitle(0);
      },
    });
  }

  private resolveUserIdentifier(): string {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const id = user.crm_id;
    return id ? String(id) : '';
  }

  private setDocumentTitle(unreadCount: number): void {
    if (unreadCount > 0) {
      document.title = `${this.baseDocumentTitle} (${unreadCount})`;
      return;
    }
    document.title = this.baseDocumentTitle;
  }
}
